/*
  LINDIE Age Gate — 18+ confirmation for restricted content.

  Two entry points, one consent:

    1. Page gate    — layout/theme.liquid put `lindie-age-locked` on <html>
                      because this collection (or the product's collection) is
                      restricted. CSS has already hidden the page. "No" leaves.

    2. Preview gate — a click on a Preview button carrying data-age-restricted.
                      The page itself is ordinary, so "No" just closes and stays.

  Consent is asked ONCE PER BROWSER and then kept for good. The stored value is
  a plain marker with no expiry — its presence is the whole test.

  Values written by the earlier 30-day build were timestamps, which are non-empty
  strings and so still read as consent. Nobody who has already confirmed is asked
  again, and no migration is needed.

  This file deliberately does NOT modify assets/lindie-audio-preview.js. It
  intercepts in the CAPTURE phase on `document`, which runs before that file's
  bubble-phase handler on the same node regardless of script load order.
*/
(function () {
  'use strict';

  var KEY = 'lindie_age_ok';
  var LOCK = 'lindie-age-locked';

  /*
    Mirrors the stored flag in memory. Without this, a browser that refuses to
    persist (private mode, blocked site data) would grant consent, re-fire the
    preview click, read no consent back, and re-open the gate — forever.
  */
  var consented = false;

  var gate = null;
  var pendingPreview = null;  /* the Preview button to resume after a Yes */
  var isPageGate = false;     /* set at open time: decides what "No" does */
  var lastFocus = null;

  /* ── Consent ─────────────────────────────────────────────────────────── */

  function hasConsent() {
    if (consented) return true;
    try {
      /* Presence is the test — no expiry. Any value the earlier build wrote is
         a non-empty timestamp string, so it still counts. */
      if (localStorage.getItem(KEY)) {
        consented = true;
        return true;
      }
    } catch (e) { /* storage unavailable — fall through to asking */ }
    return false;
  }

  function remember() {
    consented = true;
    try {
      localStorage.setItem(KEY, '1');
    } catch (e) { /* not persisted; the in-memory flag carries this page load */ }
  }

  /* ── Opening and closing ─────────────────────────────────────────────── */

  function open(previewBtn) {
    if (!gate) return;

    pendingPreview = previewBtn || null;
    isPageGate = !previewBtn;
    lastFocus = document.activeElement;

    /*
      A page gate is opaque and already scroll-locked by the CSS. A preview gate
      sits over an ordinary page, so it gets a translucent backdrop and its own
      scroll lock.
    */
    gate.classList.toggle('lag--overlay', !isPageGate);
    if (!isPageGate) document.documentElement.style.overflow = 'hidden';

    gate.hidden = false;

    var yes = gate.querySelector('[data-lindie-age-yes]');
    if (yes) yes.focus();
  }

  function close() {
    if (!gate) return;
    gate.hidden = true;
    gate.classList.remove('lag--overlay');
    document.documentElement.style.overflow = '';

    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  /* ── Answers ─────────────────────────────────────────────────────────── */

  function grant() {
    remember();

    document.documentElement.classList.remove(LOCK);  /* content appears */
    close();

    /*
      Resume the click they made. hasConsent() is true now, so the interceptor
      below returns early and the real preview handler runs — no second click.
    */
    if (pendingPreview) {
      var btn = pendingPreview;
      pendingPreview = null;
      btn.click();
    }
  }

  function deny() {
    pendingPreview = null;

    if (isPageGate) {
      /*
        A FIXED home route, never document.referrer: the referrer can be the
        restricted page itself, which would bounce them straight back in.
      */
      window.location.href = (gate && gate.getAttribute('data-lindie-age-home')) || '/';
      return;
    }

    close();  /* preview gate: nothing plays, they stay where they are */
  }

  /* ── Wiring ──────────────────────────────────────────────────────────── */

  function bind() {
    gate = document.querySelector('[data-lindie-age-gate]');
    if (!gate) return;

    /*
      The page rendered locked, so this is a page gate from the outset. CSS is
      already showing it; clearing `hidden` here only brings the attribute back
      in line with what is on screen, which matters for assistive tech and for
      focusability. No flash — the panel was visible before this ran.
    */
    if (document.documentElement.classList.contains(LOCK)) {
      isPageGate = true;
      gate.hidden = false;
      var yes = gate.querySelector('[data-lindie-age-yes]');
      if (yes) yes.focus();
    }

    gate.addEventListener('click', function (event) {
      if (event.target.closest('[data-lindie-age-yes]')) { grant(); return; }
      if (event.target.closest('[data-lindie-age-no]')) { deny(); }
    });

    document.addEventListener('keydown', function (event) {
      if (gate.hidden) return;

      if (event.key === 'Escape') {
        /* Escape must never dismiss a page gate into the restricted content. */
        if (!isPageGate) { event.preventDefault(); deny(); }
        return;
      }

      if (event.key === 'Tab') trapFocus(event);
    });
  }

  function trapFocus(event) {
    var items = gate.querySelectorAll('button:not([disabled])');
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /*
    Preview interception. Capture phase on `document` so it runs BEFORE the
    delegated handler in lindie-audio-preview.js, and stopImmediatePropagation
    rather than stopPropagation because both handlers are bound to the same node.
  */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.lindie-product-card__preview-link');
    if (!btn || btn.getAttribute('data-age-restricted') !== 'true') return;
    if (hasConsent()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    open(btn);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
